import React, { useState, useContext } from 'react';
import theme from './Header.module.scss';
import PageTitleContainer from './PageTitleContainer';
import HeaderSearch from './HeaderSearch/HeaderSearch';
import { UserIcon } from '@/components/Icons';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import HeaderProfileMenu from './HeaderProfileMenu';
import HeaderUserMenu from './HeaderUserMenu';
import NavigationContextMenu from '../NavigationContextMenu';
import { useCurrentCulture, useIsBuyer, useIsSeller, useIsWhiteLabel } from '@/utils/hooks';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import HeaderLanguageMenu from './HeaderLanguageMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { classnames } from '@/utils/html';
import HeaderContext from './HeaderContext';
import HeaderProfileThumbnail from './HeaderProfileThumbnail';
import HeaderNotificationsButton from './HeaderNotificationsButton';
import { Sticky } from '@ygtechnologies/bygr-frontend-component-library';
import { HelpkitButton } from './HelpkitButton';

interface HeaderProps {}

const Header: React.FC<HeaderProps> = () => {
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotificationsMenu, setShowNotificationsMenu] = useState(false);
    const [showLanguageMenu, setShowLanguageMenu] = useState(false);
    const isSeller = useIsSeller();
    const isBuyer = useIsBuyer();

    const [currentCulture] = useCurrentCulture();

    const profiles = useSelector((state: IState) => state.global.profiles);
    const activeProfile = useSelector((state: IState) => state.global.activeProfile);
    const activeProfileInfo = profiles?.find((x) => x.profileId === activeProfile?.id);
    const currentUser = useSelector((state: IState) => state.global.user);
    const context = useContext(HeaderContext);

    const hasSidebar = !!activeProfile;

    const isWhiteLabel = useIsWhiteLabel();

    const renderHelpKitButton = React.useMemo(() => {
        if (isBuyer && currentCulture?.showBuyerHelp) {
            return true;
        }
        if (isSeller && currentCulture?.showSellerHelp) {
            return true;
        }
        return false;
    }, [isBuyer, isSeller, currentCulture]);

    // Return after all hooks have run so we don't violate the Rules of Hooks when toggled.
    if (context.hidden) return null;

    return (
        <Sticky
            className={classnames(theme.container, {
                [theme.transparent]: context.transparent,
                [theme.hasSidebar]: hasSidebar,
                [theme.whiteLabel]: isWhiteLabel,
                [theme.isBuyer]: isBuyer,
            })}
            stickyClassName={theme.sticky}>
            <div data-cm-header-root="true" className={classnames(theme.wrapper, { [theme.isBuyer]: isBuyer })}>
                <div className={theme.left}>
                    <PageTitleContainer className={theme.title} />
                </div>

                <div className={theme.right}>
                    {currentUser?.isLoggedIn && isSeller && <HeaderSearch className={theme.search} />}

                    {renderHelpKitButton && <HelpkitButton />}

                    {activeProfile && (
                        <HeaderNotificationsButton open={showNotificationsMenu} toggle={setShowNotificationsMenu} />
                    )}

                    {currentCulture && (
                        <NavigationContextMenu
                            className={theme.languageSelector}
                            element={
                                <div className={classnames(theme.headerButton, { [theme.active]: showLanguageMenu })}>
                                    {currentCulture.localName}
                                    <FontAwesomeIcon icon={faChevronDown} rotation={showLanguageMenu ? 180 : undefined} />
                                </div>
                            }
                            open={showLanguageMenu}
                            toggle={setShowLanguageMenu}
                            noIcons
                            direction="right"
                            size="fit">
                            <HeaderLanguageMenu />
                        </NavigationContextMenu>
                    )}

                    {activeProfileInfo && (
                        <NavigationContextMenu
                            element={
                                <HeaderProfileThumbnail
                                    cacheDependencies={[activeProfile]}
                                    sizeInPx={36}
                                    profile={activeProfileInfo}
                                    className={classnames(theme.icon, { [theme.active]: showProfileMenu })}
                                />
                            }
                            size="fit"
                            open={showProfileMenu}
                            toggle={setShowProfileMenu}
                            className={theme.profileMenuButton}
                            menuClassName={theme.profileMenu}>
                            <HeaderProfileMenu />
                        </NavigationContextMenu>
                    )}

                    {currentUser?.isLoggedIn && (
                        <NavigationContextMenu
                            element={
                                <UserIcon
                                    sizeInPx={36}
                                    userId={currentUser.userId}
                                    className={classnames(theme.icon, { [theme.active]: showUserMenu })}
                                />
                            }
                            open={showUserMenu}
                            toggle={setShowUserMenu}>
                            <HeaderUserMenu />
                        </NavigationContextMenu>
                    )}
                </div>
            </div>
        </Sticky>
    );
};

export default Header;
